// Pure keyboard-event decoders shared across hooks.
//
// Each decoder takes a few primitive inputs (the key string, modifier flags,
// some piece of UI state) and returns a totally-typed action discriminant.
// No DOM access, no side effects, no module-scope state. Live here together
// so test files can import them without pulling in either hook module (both
// of which transitively load `.dox/ts/unions`).

// ── OmniSearch ──

// Pure: clamp index within [0, max]
export const clampIndex = (idx: number, delta: number, max: number): number =>
  Math.max(0, Math.min(idx + delta, max));

export type SearchKeyResult =
  | { readonly action: "select"; readonly index: number }
  | { readonly action: "navigate"; readonly url: string }
  | { readonly action: "close" }
  | { readonly action: "none" };

export const searchKeyAction = (
  key: string,
  selected: number,
  itemCount: number,
  selectedUrl: string | undefined,
): SearchKeyResult => {
  switch (key) {
    case "ArrowDown":
      return itemCount > 0
        ? { action: "select", index: clampIndex(selected, 1, itemCount - 1) }
        : { action: "none" };
    case "ArrowUp":
      return itemCount > 0
        ? { action: "select", index: clampIndex(selected, -1, itemCount - 1) }
        : { action: "none" };
    case "Enter":
      return selectedUrl
        ? { action: "navigate", url: selectedUrl }
        : { action: "none" };
    case "Escape":
      return { action: "close" };
    default:
      return { action: "none" };
  }
};

// ── Code browser ──

// Pure: map a keypress to a code-browser action.
// Modifier keys and sidebar state are passed in so this stays testable.
export type CodeBrowserAction =
  | { readonly action: "toggle_shortcuts" }
  | { readonly action: "toggle_sidebar"; readonly sidebarOpen: boolean }
  | { readonly action: "toggle_blame" }
  | { readonly action: "focus_search" }
  | { readonly action: "permalink" }
  | { readonly action: "close_shortcuts" }
  | { readonly action: "none" };

export const codeBrowserKeyAction = (
  key: string,
  ctrl: boolean,
  meta: boolean,
  alt: boolean,
  sidebarOpen: boolean,
): CodeBrowserAction => {
  if (key === "?" && !ctrl && !meta) return { action: "toggle_shortcuts" };
  if (key === "b" && (ctrl || meta))
    return { action: "toggle_sidebar", sidebarOpen };
  if (key === "b" && !ctrl && !meta && !alt) return { action: "toggle_blame" };
  if (key === "t" && !ctrl && !meta && !alt) return { action: "focus_search" };
  if (key === "y" && !ctrl && !meta && !alt) return { action: "permalink" };
  if (key === "Escape") return { action: "close_shortcuts" };
  return { action: "none" };
};
